<template>
    <div class="sc__analysis__tab__box tab-content">
        <div class="sc__analysis__head__box">
            <div class="title__box">
                <div class="head__title__group">
                    <h3 class="title">Program Targets</h3>
                </div>
                <div class="print__data__btn">
                    <WorkpacketPdf :button-label="`Download`" :assessmentId="assessmentId"
                        :organizationId="organizationId" :learnerId="learnerId"
                        v-if="assessmentId && organizationId && learnerId" />
                </div>
            </div>

            <div class="head__label__box__wrapper main__head__info__wrapper">
                <div class="head__information__wrapper">
                    <p>Info sources: Step 4c combinations, Shells info (in EESA Guide, Chapter 4 and Appendix A)</p>
                </div>
                <div class="head__information__wrapper">
                    <h6 class="info__title">Directions</h6>
                    <ul>
                        <li>Write syllable approximations (if needed) from current skill to final target.</li>
                        <li>In training, reinforce vocals at each step, moving toward the terminal target. Example:
                            “movie” in 4 steps: (1) oo (2) oo-ee (3) moo-ee (4) moo-vee</li>
                        <li><div class="rlms-flex rlms-gap-5">Download <a href="/pdf/Vowel Neighborhood.pdf" download style="display: contents;color: #725CFF; font-weight: 500;">Vowel Neighborhood <img src="@/assets/images/link.svg"/></a>.</div></li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="main__programtargets__data__box">
            <div class="programtargets_top_wrapper">
                <h5 class="title">Key: T = Target</h5>
                <div class="programtargets_top_btn_wrapper">
                    <button class="purple_outline_btn" type="button" @click="generateFromMiniProgramPlan" :disabled="generating">
                        <span v-if="generating">Generating...</span>
                        <span v-else>Generate from Mini Program Plan</span>
                    </button>
                    <button id="allTargetsClear" class="purple_outline_btn" type="button" @click="clearAll">Clear
                        Data</button>
                    <div class="section__btn__block">
                        <button id="allTargetsSave" class="purple__save__btn" type="button"
                            @click="saveNote">Save</button>
                    </div>
                </div>
            </div>

            <div id="dataContent" class="program-target-box-wrapper">
                <div class="program-target-box">
                    <div class="ptb-head">
                        <span>E.g<sub></sub></span>
                        <input type="text" value="movie" readonly disabled>
                    </div>
                    <div class="ptb-body">
                        <div class="ptb-body-item">
                            <input type="text" placeholder="..." readonly="" value="end">
                            <div>
                                <input type="text" value="moo-vee" readonly disabled>
                            </div>
                        </div>
                        <div class="ptb-body-item">
                            <input type="text" value="..." placeholder="..." readonly>
                            <div>
                                <input type="text" value="moo-ee" readonly disabled>
                            </div>
                        </div>
                        <div class="ptb-body-item">
                            <input type="text" value="..." placeholder="..." readonly>
                            <div>
                                <input type="text" value="oo-ee" readonly disabled>
                            </div>
                        </div>
                        <div class="ptb-body-item">
                            <input type="text" placeholder="..." readonly="" value="start">
                            <div>
                                <input type="text" value="oo" readonly disabled>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="program-target-box" v-for="(target, tIndex) in targets" :key="tIndex">
                    <div class="ptb-head">
                        <span>T<sub>{{ tIndex + 1 }}</sub></span>
                        <input type="text" v-model="target.header">
                    </div>
                    <div class="ptb-body">
                        <div class="ptb-body-item" v-for="(input, iIndex) in target.inputs" :key="iIndex"
                            :class="{ startInput: Object.keys(input)[0] === 'start' }">
                            <input type="text" :value="Object.keys(input)[0]" placeholder="..." readonly>
                            <div v-if="Object.keys(input)[0] === '...'">
                                <div class="ptb-bi-box">
                                    <input type="text" v-model="input['...']"/>
                                    <img :src="blade1" width="19" height="18"
                                        class="removeInput" @click="removeInput(tIndex, iIndex)" alt="">
                                </div>
                            </div>
                            <div v-else>
                                <input type="text" v-model="input[Object.keys(input)[0]]"/>
                            </div>
                        </div>
                    </div>
                    <div class="ptb-footer section__btn__block">
                        <button class="purple__save__btn addNewInput" type="button" @click="addInput(tIndex)" v-if="target.inputs?.length < 7">Add
                            Input</button>
                    </div>
                </div>

                <div id="addNewTarget" class="program-target-box program-target-add-box" @click="addTarget">
                    <img :src="blade2" width="40" height="39" alt="">
                    <span>Add T<sub>{{ targets.length + 1 }}</sub></span>
                </div>
            </div>
        </div>
    </div>
</template>
<script setup>
import { ref, onMounted, inject } from 'vue';
import api from '@/services/axios';
import { useLoaderStore } from '@/stores/loader';
import { handleError } from '@/utils/handleError';
import { aiFeaturesAllowed } from '@/utils/legal-gate';
import { showDialog } from '@/utils/swal';
import WorkpacketPdf from './WorkpacketPdf.vue';
import blade1 from '@/assets/images/tab10_blade_076d28ca.svg'
import blade2 from '@/assets/images/tab10_blade_7c52ece7.svg'
import { authrizedPermissions } from '@/utils/permissions';

const props = defineProps({
    organizationId: Number,
    assessmentId: Number,
    learnerId: Number,
    canConductAssessment: Boolean,
});

const loader = useLoaderStore();
const generating = ref(false);
const originalTargets = ref(null);

// Register with parent for unsaved changes tracking
const registerUnsavedCheck = inject('registerUnsavedCheck', null);
const hasUnsavedChanges = () => {
    if (!originalTargets.value) return false;
    return JSON.stringify(targets.value) !== JSON.stringify(originalTargets.value);
};
if (registerUnsavedCheck) {
    registerUnsavedCheck(hasUnsavedChanges);
}

const targets = ref([
    {
        header: '',
        inputs: [
            { end: '' },
            { '...': '' },
            { start: '' }
        ]
    }
]);
const assessment = ref(null);
const miniProgramPlanWords = ref([]);

const fetchAssessment = async () => {
    loader.show();
    try {
        const { data } = await api.get(`/organizations/${props.organizationId}/assessments/${props.assessmentId}`);
        assessment.value = data?.data || null
        const note = data?.data?.note?.program_targets ?? null;

        targets.value = (note) ? JSON.parse(note) : [{
            header: '',
            inputs: [
                { end: '' },
                { '...': '' },
                { start: '' }
            ]
        }];

        // Store original data for unsaved changes detection
        originalTargets.value = JSON.parse(JSON.stringify(targets.value));

        // Also fetch mini_program_plan words for the generate feature
        const miniPlanNote = data?.data?.note?.mini_program_plan ?? null;
        miniProgramPlanWords.value = miniPlanNote ? JSON.parse(miniPlanNote) : [];
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
};

const saveNote = async () => {
    if(!props.canConductAssessment) {
        let message = `You don’t have permission perform this action.`;
        const learner = assessment.value?.learner;
        if (learner?.is_administrator) {
            message = `You don’t have permission perform this action because it was created before you were granted administrator access.`;
        } else if (learner?.is_authorized_user) {
            if (learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission perform this action because it was created before you were granted group access.`;
            } else if (!learner?.permissions?.[authrizedPermissions.conduct_view_assessments]) {
                message = `You don’t have permission perform this action.`;
            }
        }

        showDialog({ icon: 'warning', html: message });
        return;
    }

    loader.show();
    try {
        const output = targets.value.map((target) => {
            const inputs = target.inputs.map((input) => {
                const key = Object.keys(input)[0];
                return { [key]: input[key] || null };
            });
            return {
                header: target.header,
                inputs
            };
        });

        await api.post(`/organizations/${props.organizationId}/assessments/${props.assessmentId}/notes`, {
            program_targets: JSON.stringify(output)
        });

        // Update original data after successful save
        originalTargets.value = JSON.parse(JSON.stringify(targets.value));

        showDialog({
            title: 'Success',
            icon: 'success',
            html: 'Program Targets saved successfully!'
        });
    } catch (error) {
        handleError(error);
    } finally {
        loader.hide();
    }
}

const addTarget = () => {
    targets.value.push({
        header: '',
        inputs: [
            { end: '' },
            { '...': '' },
            { start: '' }
        ]
    });
};

const removeInput = (targetIndex, inputIndex) => {
    const t = targets.value[targetIndex];
    const inputKey = Object.keys(t.inputs[inputIndex])[0];

    if (inputKey === '...') {
        t.inputs.splice(inputIndex, 1);
    }
};

const addInput = (targetIndex) => {
    const inputs = targets.value[targetIndex].inputs;

    // Limit to max 5
    if (inputs.length >= 7) {
        console.warn("Maximum of 5 inputs reached");
        return;
    }

    // Find the index of the object with key "end" safely
    const endIndex = inputs.findIndex(obj => Object.prototype.hasOwnProperty.call(obj, "end"));

    // Insert after "end" or push at the end if not found
    if (endIndex !== -1) {
        inputs.splice(endIndex + 1, 0, { '...': '' });
    } else {
        inputs.push({ '...': '' });
    }
};



const clearAll = () => {
    targets.value = [
        {
            header: '',
            inputs: [
                { end: '' },
                { '...': '' },
                { start: '' }
            ]
        }
    ];
};

const generateFromMiniProgramPlan = async () => {
    if (!aiFeaturesAllowed()) return;

    // Check if mini program plan has words
    if (!miniProgramPlanWords.value || miniProgramPlanWords.value.length === 0) {
        showDialog({
            icon: 'warning',
            title: 'No Words Available',
            html: 'Please head to Mini Program Plan to add verbal targets first.'
        });
        return;
    }

    // Count existing targets that have content (header is not empty)
    const existingTargetsCount = targets.value.filter(t => t.header && t.header.trim() !== '').length;

    // Check if already at max 8
    if (existingTargetsCount >= 8) {
        showDialog({
            icon: 'info',
            title: 'Maximum Targets Reached',
            html: 'You already have 8 targets. Please remove some targets first if you want to generate new ones.'
        });
        return;
    }

    // Calculate how many we can add
    const slotsAvailable = 8 - existingTargetsCount;

    // Get words that aren't already in targets (avoid duplicates)
    const existingWords = targets.value.map(t => t.header?.toLowerCase()).filter(Boolean);
    const newWords = miniProgramPlanWords.value.filter(
        w => !existingWords.includes(w.word?.toLowerCase())
    );

    if (newWords.length === 0) {
        showDialog({
            icon: 'info',
            title: 'All Words Already Added',
            html: 'All words from Mini Program Plan are already in your targets.'
        });
        return;
    }

    // Take only what we can fit (up to 8 total)
    const wordsToProcess = newWords.slice(0, slotsAvailable);

    generating.value = true;
    try {
        const { data } = await api.post('/open-ai/program-targets/generate', {
            words: wordsToProcess.map(w => ({
                word: w.word,
                ipa: w.ipa || null,
                consonants: w.consonants || null,
                vowels: w.vowels || null
            }))
        });

        if (data.results && data.results.length > 0) {
            // If first target is empty (default state), replace it
            const firstTargetEmpty = targets.value.length === 1 &&
                (!targets.value[0].header || targets.value[0].header.trim() === '');

            if (firstTargetEmpty) {
                targets.value = data.results;
            } else {
                // Append new targets
                targets.value = [...targets.value, ...data.results];
            }

            showDialog({
                icon: 'success',
                title: 'Targets Generated!',
                html: `Successfully generated ${data.results.length} target(s) from Mini Program Plan.`
            });
        }
    } catch (error) {
        handleError(error);
    } finally {
        generating.value = false;
    }
};

onMounted(fetchAssessment);
</script>